/**
 * BGPView investigation collector.
 *
 * Queries the BGPView API for network topology data — ASN details,
 * IP prefixes, and peer relationships. BGPView is free with no API key
 * required, so this collector calls the API directly (no edge function proxy).
 *
 * API documentation: https://bgpview.docs.apiary.io
 */

import type { CollectionTask } from '../types';

// ---------------------------------------------------------------------------
// BGPView API
// ---------------------------------------------------------------------------

const BGPVIEW_API = 'https://api.bgpview.io';

/** Default timeout for BGPView queries (ms). */
const DEFAULT_TIMEOUT_MS = 15_000;

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface BGPViewASN {
  asn: number;
  name: string;
  description_short: string;
  country_code: string;
  website?: string;
  email_contacts?: string[];
  looking_glass?: string;
  traffic_estimation?: string;
  rir_allocation?: {
    rir_name: string;
    country_code: string;
    date_allocated: string;
  };
}

export interface BGPViewPrefix {
  prefix: string;
  ip: string;
  cidr: number;
  name: string;
  description_short: string;
  country_code: string;
  parent?: {
    prefix: string;
    name: string;
  };
}

export interface BGPViewPeer {
  asn: number;
  name: string;
  description: string;
  country_code: string;
}

export interface BGPViewCollectionResult {
  rawText: string;
  asn?: BGPViewASN;
  prefixes: BGPViewPrefix[];
  peers: BGPViewPeer[];
  queryType: 'asn' | 'ip' | 'prefix' | 'search';
}

// ---------------------------------------------------------------------------
// Internal helpers
// ---------------------------------------------------------------------------

/** Regex patterns for query target detection. */
const ASN_PATTERN = /\bAS[N]?\s*(\d{1,10})\b/i;
const IP_PATTERN = /\b(\d{1,3}\.\d{1,3}\.\d{1,3}\.\d{1,3})\b/;
const CIDR_PATTERN = /\b(\d{1,3}\.\d{1,3}\.\d{1,3}\.\d{1,3}\/\d{1,2})\b/;

interface QueryTarget {
  type: 'asn' | 'ip' | 'prefix' | 'search';
  value: string;
}

/**
 * Determine the query type from task keywords.
 * Checks for ASN numbers, IP addresses, CIDR prefixes, and falls back
 * to a text search with the first 3 keywords.
 */
function parseQueryTarget(task: CollectionTask): QueryTarget {
  const query = task.query as Record<string, unknown>;
  const keywords = (query.keywords as string[]) ?? [];
  const joined = keywords.join(' ');

  // Check for ASN pattern first (e.g., "AS13335", "ASN 13335")
  const asnMatch = joined.match(ASN_PATTERN);
  if (asnMatch) {
    return { type: 'asn', value: asnMatch[1] };
  }

  // Check for CIDR prefix (e.g., "1.1.1.0/24") — before bare IP
  const cidrMatch = joined.match(CIDR_PATTERN);
  if (cidrMatch) {
    return { type: 'ip', value: cidrMatch[1].split('/')[0] };
  }

  // Check for bare IP address (e.g., "1.1.1.1")
  const ipMatch = joined.match(IP_PATTERN);
  if (ipMatch) {
    return { type: 'ip', value: ipMatch[1] };
  }

  // Fallback: text search with first 3 keywords
  const searchTerms = keywords
    .filter(k => k.length > 0)
    .slice(0, 3)
    .join(' ');

  if (!searchTerms) {
    throw new Error('BGPView query requires at least one keyword');
  }

  return { type: 'search', value: searchTerms };
}

/**
 * Fetch JSON from a URL with timeout and standard headers.
 * BGPView wraps all responses in `{ data: T }`, so this extracts `.data`.
 */
async function fetchJSON<T>(url: string, timeoutMs: number): Promise<T> {
  const response = await globalThis.fetch(url, {
    headers: {
      'User-Agent': 'Sentinel/1.0 OSINT Platform',
      Accept: 'application/json',
    },
    signal: AbortSignal.timeout(timeoutMs),
  });

  if (!response.ok) {
    throw new Error(`BGPView API returned ${response.status}: ${response.statusText}`);
  }

  const json = (await response.json()) as { data: T };
  return json.data;
}

// ---------------------------------------------------------------------------
// Query type handlers
// ---------------------------------------------------------------------------

/**
 * Collect ASN details, prefixes, and peers for a given ASN number.
 */
async function collectASN(
  asnNumber: string,
  timeoutMs: number,
): Promise<BGPViewCollectionResult> {
  // Fetch ASN details, prefixes, and peers in parallel
  const [asnData, prefixData, peerData] = await Promise.all([
    fetchJSON<BGPViewASN>(`${BGPVIEW_API}/asn/${asnNumber}`, timeoutMs),
    fetchJSON<{ ipv4_prefixes?: BGPViewPrefix[] }>(
      `${BGPVIEW_API}/asn/${asnNumber}/prefixes`,
      timeoutMs,
    ).catch(() => ({ ipv4_prefixes: [] as BGPViewPrefix[] })),
    fetchJSON<{ ipv4_peers?: BGPViewPeer[] }>(
      `${BGPVIEW_API}/asn/${asnNumber}/peers`,
      timeoutMs,
    ).catch(() => ({ ipv4_peers: [] as BGPViewPeer[] })),
  ]);

  const prefixes = (prefixData.ipv4_prefixes ?? []).slice(0, 50);
  const peers = (peerData.ipv4_peers ?? []).slice(0, 30);

  // Build raw text for entity extraction
  const textParts: string[] = [];

  textParts.push(`ASN Details: AS${asnData.asn}`);
  textParts.push(`Name: ${asnData.name}`);
  textParts.push(`Description: ${asnData.description_short}`);
  textParts.push(`Country: ${asnData.country_code}`);

  if (asnData.website) {
    textParts.push(`Website: ${asnData.website}`);
  }
  if (asnData.traffic_estimation) {
    textParts.push(`Traffic estimation: ${asnData.traffic_estimation}`);
  }
  if (asnData.rir_allocation) {
    textParts.push(
      `RIR: ${asnData.rir_allocation.rir_name}, allocated ${asnData.rir_allocation.date_allocated}`,
    );
  }

  if (prefixes.length > 0) {
    textParts.push('');
    textParts.push(`IPv4 Prefixes (${prefixes.length} total):`);
    for (const p of prefixes.slice(0, 20)) {
      textParts.push(`  ${p.prefix} — ${p.name || p.description_short || 'unnamed'} (${p.country_code})`);
    }
    if (prefixes.length > 20) {
      textParts.push(`  ... and ${prefixes.length - 20} more prefixes`);
    }
  }

  if (peers.length > 0) {
    textParts.push('');
    textParts.push(`Peers (${peers.length} total):`);
    for (const p of peers.slice(0, 15)) {
      textParts.push(`  AS${p.asn} — ${p.name} (${p.country_code})`);
    }
    if (peers.length > 15) {
      textParts.push(`  ... and ${peers.length - 15} more peers`);
    }
  }

  return {
    rawText: textParts.join('\n'),
    asn: asnData,
    prefixes,
    peers,
    queryType: 'asn',
  };
}

/**
 * Collect prefix and ASN information for a given IP address.
 */
async function collectIP(
  ipAddress: string,
  timeoutMs: number,
): Promise<BGPViewCollectionResult> {
  interface IPResponse {
    ip: string;
    prefixes: Array<{
      prefix: string;
      ip: string;
      cidr: number;
      asn: { asn: number; name: string; description: string; country_code: string };
      name: string;
      description_short: string;
      country_code: string;
    }>;
  }

  const data = await fetchJSON<IPResponse>(`${BGPVIEW_API}/ip/${ipAddress}`, timeoutMs);

  const prefixes: BGPViewPrefix[] = (data.prefixes ?? []).map(p => ({
    prefix: p.prefix,
    ip: p.ip,
    cidr: p.cidr,
    name: p.name,
    description_short: p.description_short,
    country_code: p.country_code,
  }));

  // Build raw text
  const textParts: string[] = [];
  textParts.push(`IP Address: ${data.ip}`);

  if (data.prefixes?.length > 0) {
    textParts.push('');
    textParts.push('Announced Prefixes:');
    for (const p of data.prefixes) {
      textParts.push(`  ${p.prefix} — AS${p.asn.asn} ${p.asn.name} (${p.asn.country_code})`);
      if (p.asn.description) {
        textParts.push(`    ${p.asn.description}`);
      }
    }
  } else {
    textParts.push('No announced prefixes found for this IP.');
  }

  return {
    rawText: textParts.join('\n'),
    prefixes,
    peers: [],
    queryType: 'ip',
  };
}

/**
 * Search BGPView for ASNs matching a text query.
 */
async function collectSearch(
  searchTerm: string,
  timeoutMs: number,
): Promise<BGPViewCollectionResult> {
  interface SearchResponse {
    asns?: Array<{
      asn: number;
      name: string;
      description: string;
      country_code: string;
    }>;
  }

  const encoded = encodeURIComponent(searchTerm);
  const data = await fetchJSON<SearchResponse>(
    `${BGPVIEW_API}/search?query_term=${encoded}`,
    timeoutMs,
  );

  const asns = (data.asns ?? []).slice(0, 20);

  // Build raw text
  const textParts: string[] = [];
  textParts.push(`BGPView search: "${searchTerm}"`);

  if (asns.length > 0) {
    textParts.push('');
    textParts.push(`Found ${asns.length} ASN results:`);
    for (const a of asns) {
      textParts.push(`  AS${a.asn} — ${a.name} (${a.country_code})`);
      if (a.description) {
        textParts.push(`    ${a.description}`);
      }
    }
  } else {
    textParts.push('No results found.');
  }

  return {
    rawText: textParts.join('\n'),
    prefixes: [],
    peers: [],
    queryType: 'search',
  };
}

// ---------------------------------------------------------------------------
// Main collector
// ---------------------------------------------------------------------------

/**
 * Collect network topology data from BGPView for an investigation task.
 * Determines query type from task keywords and dispatches to the
 * appropriate handler.
 */
export async function collectFromBGPView(
  task: CollectionTask,
  timeoutMs: number = DEFAULT_TIMEOUT_MS,
): Promise<BGPViewCollectionResult> {
  const target = parseQueryTarget(task);

  switch (target.type) {
    case 'asn':
      return collectASN(target.value, timeoutMs);
    case 'ip':
    case 'prefix':
      return collectIP(target.value, timeoutMs);
    case 'search':
      return collectSearch(target.value, timeoutMs);
  }
}
